import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { Loader2, Filter } from "lucide-react";
import PlatformBadge from "@/components/PlatformBadge";

const API = `${getApiUrl()}/api`;

const CHART_COLORS = {
  hourly: "#06B6D4",
  daily: "#3B82F6",
  monthly: "#8B5CF6",
  yearly: "#F59E0B",
  seasonal: "#10B981",
  platform: "#EC4899",
  system: "#F97316",
};

const TITLES = {
  hourly: (v) => `Hour ${v}:00 Breakdown`,
  daily: (v) => `${v} Breakdown`,
  monthly: (v) => `${v} Breakdown`,
  yearly: (v) => `Year ${v} Breakdown`,
  seasonal: (v) => `${v} Breakdown`,
  platform: (v) => `${v} Breakdown`,
  system: (v) => `${v} Breakdown`,
};

const SUB_LABELS = {
  hourly: "Minute-by-minute",
  daily: "Hour-by-hour",
  monthly: "Day-by-day",
  yearly: "Month-by-month",
  seasonal: "Month-by-month",
  platform: "By system",
  system: "By game",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    const d = payload[0]?.payload || {};
    return (
      <div className="bg-black/95 border border-white/20 rounded-lg px-3 py-2 max-w-xs">
        <p className="text-white font-medium text-sm">{label || d.label}</p>
        <p className="text-cyan-400 text-sm">{d.count?.toLocaleString()} achievements</p>
        {d.score > 0 && <p className="text-yellow-400 text-sm">{d.score?.toLocaleString()} score</p>}
      </div>
    );
  }
  return null;
};

export default function StatsDrilldownDialog({ open, onOpenChange, chartType, value, tzOffset = 0, platform }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !chartType || value === null || value === undefined) return;
    setLoading(true);
    const params = new URLSearchParams({
      chart_type: chartType,
      value: String(value),
      tz_offset: String(tzOffset),
    });
    if (platform) params.append("platform", platform);

    axios
      .get(`${API}/stats/drilldown?${params}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, chartType, value, tzOffset, platform]);

  const title = TITLES[chartType]?.(value) || `${value} Breakdown`;
  const subLabel = SUB_LABELS[chartType] || "Breakdown";
  const color = CHART_COLORS[chartType] || "#8B5CF6";
  const breakdown = data?.breakdown || [];
  const platforms = data?.platformSummary || [];
  const systems = data?.systemSummary || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="stats-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{title}</DialogTitle>
          <DialogDescription className="text-white/40 text-sm">
            {subLabel} &middot; {data?.totalCount?.toLocaleString() || 0} achievements &middot; {data?.totalScore?.toLocaleString() || 0} score
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-white/30" />
          </div>
        ) : !data || breakdown.length === 0 ? (
          <div className="py-12 text-center text-white/30">No data for this selection</div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-4">
            {/* Sub-breakdown chart */}
            <div className="h-56" data-testid="drilldown-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={breakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis
                    dataKey="label"
                    stroke="#52525B"
                    fontSize={breakdown.length > 30 ? 8 : 10}
                    tick={{ fill: "#E5E5E5" }}
                    interval={breakdown.length > 40 ? Math.floor(breakdown.length / 15) : breakdown.length > 20 ? 1 : 0}
                    angle={breakdown.length > 15 ? -45 : 0}
                    textAnchor={breakdown.length > 15 ? "end" : "middle"}
                    height={breakdown.length > 15 ? 50 : 30}
                  />
                  <YAxis stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {breakdown.map((entry, i) => {
                      const max = Math.max(...breakdown.map((b) => b.count));
                      const ratio = entry.count / (max || 1);
                      const opacity = 0.3 + ratio * 0.7;
                      return <Cell key={i} fill={color} fillOpacity={opacity} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* View in Achievements button */}
            {(chartType === "platform" || chartType === "system") && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
                  data-testid="drilldown-view-achievements"
                  onClick={() => {
                    const params = new URLSearchParams({ status: "unlocked" });
                    if (chartType === "platform" && value) params.set("platform", value);
                    if (platform) params.set("platform", platform);
                    onOpenChange(false);
                    navigate(`/achievements?${params.toString()}`);
                  }}
                >
                  <Filter className="w-3.5 h-3.5 mr-1.5" />
                  View in Achievements
                </Button>
              </div>
            )}

            {/* Unified detail table with per-row platform breakdown */}
            <div data-testid="drilldown-table">
              <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Detail ({breakdown.length} items)</h4>
              {(() => {
                // Collect all platform keys across all breakdown items
                const allPlatforms = [...new Set(
                  breakdown.flatMap(item => Object.keys(item.platforms || {}))
                )].sort((a, b) => a.localeCompare(b));

                return (
                  <div className="overflow-auto max-h-96">
                    <div className="min-w-max">
                      {/* Header row - sticky so it stays visible during vertical scroll */}
                      {allPlatforms.length > 1 && (
                        <div className="flex items-center gap-0 text-[10px] text-white/40 uppercase tracking-wider border-b border-white/5 pb-1.5 mb-1 sticky top-0 bg-[#0A0A0A] z-10">
                          <span className="w-20 shrink-0 pl-2">{subLabel.split("-")[0] || "Time"}</span>
                          {allPlatforms.map(p => (
                            <span key={p} className="w-32 shrink-0 text-center flex items-center justify-center gap-1">
                              <PlatformBadge platform={p} size="xs" />
                            </span>
                          ))}
                          <span className="w-32 shrink-0 text-center text-cyan-400/70">Total</span>
                        </div>
                      )}
                      {/* Data rows */}
                      <div className="space-y-0.5">
                        {breakdown.map((item, i) => (
                          <div key={i} className="flex items-center gap-0 py-1.5 rounded hover:bg-white/5 text-xs" data-testid={`drilldown-row-${i}`}>
                            <span className="w-20 shrink-0 text-white/70 font-medium pl-2 truncate">{item.label}</span>
                            {allPlatforms.length > 1 ? (
                              <>
                                {allPlatforms.map(p => {
                                  const pd = item.platforms?.[p];
                                  return (
                                    <span key={p} className="w-32 shrink-0 text-center">
                                      {pd ? (
                                        <span className="text-white/50">
                                          <span className="text-yellow-400/70 font-mono">{pd.score.toLocaleString()}</span>
                                          <span className="text-white/20 mx-0.5">/</span>
                                          <span className="text-cyan-400/70 font-mono">{pd.count}</span>
                                        </span>
                                      ) : (
                                        <span className="text-white/10">—</span>
                                      )}
                                    </span>
                                  );
                                })}
                                <span className="w-32 shrink-0 text-center">
                                  <span className="text-yellow-400 font-mono">{item.score.toLocaleString()}</span>
                                  <span className="text-white/20 mx-0.5">/</span>
                                  <span className="text-cyan-400 font-mono">{item.count}</span>
                                </span>
                              </>
                            ) : (
                              <div className="flex items-center gap-4 ml-auto text-right pr-2">
                                <span className="text-cyan-400 font-mono w-16">{item.count.toLocaleString()}</span>
                                {item.score > 0 && (
                                  <span className="text-yellow-400/70 font-mono w-20">{item.score.toLocaleString()} pts</span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
